<script>
import { SwIconButton } from '@getswish/design-library-web';
import { mapState, mapActions } from 'vuex';
import SectionsMenu from './SectionsMenu.vue';
import NavButton from '../../NavButton.vue';

export default {
  name: 'AppTopbar',

  components: {
    SectionsMenu,
    SwIconButton,
    NavButton,
  },

  props: {
    isApiPage: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    ...mapState('menu', ['sectionsMenu']),

    title() {
      return this.isApiPage ? 'API Reference' : 'Developer Documentation';
    },

    isSearchPage() {
      return this.$route.name === 'page-search';
    },
  },

  methods: {
    ...mapActions('settings', ['openSidebar']),
  },
};
</script>

<template>
  <div class="topbar" :class="{ api: isApiPage }">
    <div class="topbar-inner">
      <div class="left">
        <router-link :to="{ name: 'home-documentation' }">
          <img
            class="logo logo-mobile"
            src="@/assets/images/swish-icon.svg"
            alt="Swish logotype"
            width="24"
            height="24"
          />

          <img
            class="logo logo-desktop"
            src="@/assets/images/swish-logotype.svg"
            alt="Swish logotype"
            width="115"
            height="35"
          />
        </router-link>

        <span class="title title-desktop">{{ title }}</span>
        <span v-if="!sectionsMenu" class="title title-mobile">{{ title }}</span>

        <sections-menu />
      </div>

      <div class="right">
        <router-link class="search" :to="{ name: 'page-search' }" aria-label="Go to search page">
          <SwIconButton :variant="isSearchPage ? 'primary' : 'secondary'" iconName="search" />
        </router-link>

        <div class="buttons-desktop">
          <router-link v-if="isApiPage" :to="{ name: 'home-documentation' }">
            <NavButton variant="documentation" />
          </router-link>

          <router-link v-else :to="{ name: 'home-api-reference' }">
            <NavButton variant="apiReference" />
          </router-link>
        </div>

        <SwIconButton
          iconName="menu"
          class="menu-button"
          @click="openSidebar()"
          aria-label="Open Sidebar"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.topbar {
  background-color: #fff;
  border-bottom: 1px solid $separator-color;
  z-index: 997;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;

  &.api {
    background-color: $background-color-secondary;
  }

  .topbar-inner {
    max-width: $page-width + $left-sidebar-width;
    padding: 0 $space-24;
    min-height: $topbar-height-small;
    margin-bottom: -1px; // Make border-bottom visible
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include tablet-large {
      padding: 0 $space-40;
      min-height: $topbar-height-large;
    }
  }

  .left {
    display: flex;
    align-items: center;

    .logo {
      display: block;
      min-width: 24px;
      margin-right: $space-16;

      @include tablet-large {
        margin-right: $space-32;
      }
    }

    .logo-mobile {
      @include tablet-large {
        display: none;
      }
    }

    .logo-desktop {
      display: none;

      @include tablet-large {
        display: block;
      }
    }
  }

  .right {
    display: flex;
    align-items: center;

    .search {
      margin-right: $space-8;

      @include tablet-large {
        margin-right: $space-24;
      }
    }
  }

  .buttons-desktop {
    display: none;

    @include tablet-large {
      display: block;
    }
  }

  .title {
    font-weight: $font-weight-medium;
  }

  .title-desktop {
    display: none;

    @include desktop-small {
      display: block;
    }
  }

  .title-mobile {
    @include desktop-small {
      display: none;
    }
  }

  .sections-menu-mobile {
    .select {
      @include input-field-select;
      padding: $space-12 0;
      border: none;
      background-color: transparent;
      color: $text-color-secondary;
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
      padding-right: $space-20;
      max-width: 12.5rem;
      //Font size must be 16px at least to avoid zooming in on iPhone, this is a fix.
      transform: scale(0.875);
      transform-origin: left;

      &::-ms-expand {
        display: none;
      }
    }

    @include desktop-small {
      display: none;
    }
  }

  .menu-button {
    @include tablet-large {
      display: none;
    }
  }
}

@media print {
  .topbar {
    display: none;
  }
}
</style>
